import React, { useState } from "react";
import {
  View,
  Pressable,
  Modal,
  TouchableWithoutFeedback,
  StyleSheet,
  ScrollView,
  TextInput,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import Text from "@components/CustomText";
import color from "@constants/color";
import { width } from "@constants/metrics";
import { submitOtpRequest } from "@services/api";
import ToastMessage from "@components/Toast.js";
import analytics from "@services/analytics";
import { MY_TASK_EVENTS } from "@constants/analyticsEventsCT.js";

interface OtpRequestReason {
  key: string;
  label: string;
}

interface OtpRequestButtonProps {
  isEnabled: boolean;
  reasons?: OtpRequestReason[];
  orderIds?: number[];
  deliveryDate?: string;
  cxId?: string;
  cxPhoneNumber?: string;
}

const OtpRequestButton: React.FC<OtpRequestButtonProps> = ({
  isEnabled,
  reasons = [],
  orderIds = [],
  deliveryDate,
  cxId,
  cxPhoneNumber,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [customReason, setCustomReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  if (!isEnabled) return null;

  const isOthersReasonEmpty =
    selectedReason === "OTHERS" && customReason.trim().length === 0;

  const handleSubmit = async () => {
    if (!selectedReason || isOthersReasonEmpty) return;

    setIsSubmitting(true);
    try {
      await submitOtpRequest({
        delivery_date: deliveryDate,
        order_ids: orderIds,
        cx_id: cxId,
        cx_phone_number: cxPhoneNumber,
        request_reason: selectedReason,
        custom_reason: selectedReason === "OTHERS" ? customReason.trim() : null,
      });
      analytics.trackEvent(MY_TASK_EVENTS.OTP_REQUEST_SUBMITTED, {
        reason: selectedReason,
      });
      setIsSubmitted(true);
      setShowModal(false);
      setSelectedReason(null);
      setCustomReason("");
      ToastMessage("success", "OTP request submitted successfully");
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { error?: string; message?: string } };
      };
      const errorMsg =
        axiosErr?.response?.data?.error || axiosErr?.response?.data?.message;
      if (
        errorMsg === "REQUEST_ALREADY_RAISED" ||
        errorMsg === "Request already raised"
      ) {
        setIsSubmitted(true);
        ToastMessage("info", "Request already raised");
      } else {
        analytics.trackEvent(MY_TASK_EVENTS.OTP_REQUEST_SUBMIT_FAILED, {
          error: errorMsg,
        });
        ToastMessage("error", errorMsg || "Something went wrong");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onClose = () => {
    setShowModal(false);
    setSelectedReason(null);
    setCustomReason("");
  };

  return (
    <View>
      <Pressable
        onPress={() => {
          analytics.trackEvent(MY_TASK_EVENTS.OTP_REQUEST_BTN_PRESS);
          setShowModal(true);
        }}
        disabled={isSubmitted}
        testID="otp-request-button"
      >
        <View
          style={[
            styles.btnContainer,
            isSubmitted && styles.btnContainerDisabled,
          ]}
        >
          <Text style={[styles.btnText, isSubmitted && styles.btnTextDisabled]}>
            {isSubmitted ? "OTP Request Raised" : "Request OTP from Executive"}
          </Text>
        </View>
      </Pressable>

      <Modal
        animationType="fade"
        transparent={true}
        visible={showModal}
        onRequestClose={onClose}
        statusBarTranslucent
        navigationBarTranslucent
      >
        <KeyboardAvoidingView
          style={styles.flex}
          behavior={Platform.OS === "ios" ? "padding" : "height"}
        >
          <TouchableWithoutFeedback onPress={onClose}>
            <View style={styles.modalBackground} />
          </TouchableWithoutFeedback>
          <View style={styles.modalContent}>
            <Text variant="title_16" style={styles.modalTitle}>
              CX not giving OTP
            </Text>
            <Text variant="body_text_4" style={styles.modalSubtitle}>
              Executive will share OTP in ~1hr after raising request
            </Text>

            <Text variant="body_text_4" style={styles.modalSubtitle}>
              Select a reason
            </Text>

            <ScrollView style={styles.reasonsList}>
              {reasons.map((item) => (
                <Pressable
                  key={item.key}
                  style={[
                    styles.reasonItem,
                    selectedReason === item.key && styles.reasonItemSelected,
                  ]}
                  onPress={() => setSelectedReason(item.key)}
                  testID={`otp-request-reason-${item.key}`}
                >
                  <View style={styles.radioOuter}>
                    {selectedReason === item.key && (
                      <View style={styles.radioInner} />
                    )}
                  </View>
                  <Text
                    variant="body_text_2"
                    style={[
                      styles.reasonText,
                      selectedReason === item.key && styles.reasonTextSelected,
                    ]}
                  >
                    {item.label}
                  </Text>
                </Pressable>
              ))}
            </ScrollView>

            {selectedReason === "OTHERS" && (
              <TextInput
                style={styles.textInput}
                placeholder="Type your reason"
                value={customReason}
                onChangeText={setCustomReason}
                multiline={false}
                testID="otp-request-custom-reason"
              />
            )}

            <View style={styles.buttonRow}>
              <Pressable style={styles.cancelBtn} onPress={onClose}>
                <Text variant="body_text_2" style={styles.cancelBtnText}>
                  Cancel
                </Text>
              </Pressable>
              <Pressable
                style={[
                  styles.submitBtn,
                  (!selectedReason || isOthersReasonEmpty) &&
                    styles.submitBtnDisabled,
                ]}
                onPress={handleSubmit}
                disabled={
                  !selectedReason || isOthersReasonEmpty || isSubmitting
                }
                testID="otp-request-submit"
              >
                {isSubmitting ? (
                  <ActivityIndicator size="small" color={color.white} />
                ) : (
                  <Text variant="body_text_2" style={styles.submitBtnText}>
                    Submit
                  </Text>
                )}
              </Pressable>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
};

export default OtpRequestButton;

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  btnContainer: {
    paddingVertical: 5,
    paddingHorizontal: 15,
    paddingBottom: 7,
    elevation: 5,
    borderWidth: 1,
    borderRadius: 20,
    borderColor: color.blue,
    backgroundColor: color.lightGray2,
  },
  btnContainerDisabled: {
    borderColor: color.darkGrey,
    backgroundColor: color.disableGrey,
    elevation: 0,
  },
  btnText: {
    textAlign: "center",
    fontWeight: "bold",
    color: color.blue,
    fontSize: 12,
  },
  btnTextDisabled: {
    color: color.disableFont,
  },
  modalBackground: {
    flex: 1,
    backgroundColor: color.mediumBlacklightBlack,
  },
  modalContent: {
    position: "absolute",
    top: "20%",
    left: "50%",
    transform: [{ translateX: -(width - 48) / 2 }, { translateY: 0 }],
    backgroundColor: color.white,
    width: width - 48,
    borderRadius: 16,
    padding: 16,
    maxHeight: "60%",
  },
  modalTitle: {
    color: color.mediumBlack,
    textAlign: "center",
    marginBottom: 4,
  },
  modalSubtitle: {
    color: color.lightMediumBlack,
    textAlign: "center",
    marginBottom: 12,
  },
  reasonsList: {
    maxHeight: 300,
  },
  reasonItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 8,
    marginBottom: 4,
  },
  reasonItemSelected: {
    backgroundColor: color.lightGray2,
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: color.lightMediumBlack,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: color.blue,
  },
  reasonText: {
    flex: 1,
    color: color.mediumBlack,
  },
  reasonTextSelected: {
    color: color.blue,
    fontWeight: "bold",
  },
  textInput: {
    borderWidth: 1,
    borderColor: color.lightMediumBlack,
    borderRadius: 8,
    padding: 10,
    marginTop: 8,
    marginBottom: 8,
    fontSize: 14,
  },
  buttonRow: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginTop: 12,
  },
  cancelBtn: {
    flex: 1,
    marginRight: 6,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: "center",
    backgroundColor: color.lighterBlack,
  },
  cancelBtnText: {
    color: color.mediumBlack,
  },
  submitBtn: {
    flex: 1,
    marginLeft: 6,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: "center",
    backgroundColor: color.btnGreen,
  },
  submitBtnDisabled: {
    opacity: 0.5,
  },
  submitBtnText: {
    color: color.white,
  },
});
